import React, { useState, useEffect, useMemo} from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useExpensesByCategoryYearByAccount, useIncomeByCategoryYearByAccount, useSummaryTransactionsCategory } from 'hooks/transactions.hook';
import { PieChart, Pie, Cell } from 'recharts';
import { currency, date } from 'helpers/formatter';
import { TableBasicClick } from 'components';

export { TransactionReport };

function TransactionReport(props) {
  
  const [colorsExpenses, setColorsExpenses] = useState([]);
  const [colorsIncome, setColorsIncome] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState({});
  const [selectedCategoryType, setSelectedCategoryType] = useState("");
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [transactionTotal, setTransactionTotal] = useState(0);
  const [transactionTotalDeposit, setTransactionTotalDeposit] = useState(0);
  const [transactionTotalWithdrawal, setTransactionTotalWithdrawal] = useState(0);

  const handleTransactionClose = () => setShowTransactionDialog(false);

  const { data: chartExpensesData, isLoading: isChartExpensesLoading } = useExpensesByCategoryYearByAccount(props.accountId, props.report.year, props.report.month);
  const { data: chartIncomeData, isLoading: isChartIncomeLoading } = useIncomeByCategoryYearByAccount(props.accountId, props.report.year, props.report.month);
  const { chartExpenses, chartIncome } = useMemo(() => {
    const expensesByName = new Map((chartExpensesData || []).map((item) => [item.name, item]));
    const incomeByName = new Map((chartIncomeData || []).map((item) => [item.name, item]));

    return {
      chartExpenses: (chartExpensesData || []).reduce((result, expense) => {
        const income = incomeByName.get(expense.name);
        const value = income
          ? Number(expense.value) - Number(income.value)
          : Number(expense.value);
        if (value > 0) {
          result.push({ ...expense, value });
        }
        return result;
      }, []).sort((a, b) => b.value - a.value),
      chartIncome: (chartIncomeData || []).reduce((result, income) => {
        const expense = expensesByName.get(income.name);
        const value = expense
          ? Number(income.value) - Number(expense.value)
          : Number(income.value);
        if (value > 0) {
          result.push({ ...income, value });
        }
        return result;
      }, []).sort((a, b) => b.value - a.value),
    };
  }, [chartExpensesData, chartIncomeData]);

  useEffect(() => { 
    if (chartExpensesData !== undefined) {
      let colorsRand = [];
      chartExpensesData.map((e) => {
        colorsRand.push('#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0'));
      });
      setColorsExpenses(colorsRand);
    }
  }, [chartExpensesData]);

  useEffect(() => { 
    if (chartIncomeData !== undefined) {
      let colorsRand = [];
      chartIncomeData.map((e) => {
        colorsRand.push('#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0'));
      });
      setColorsIncome(colorsRand);
    }
  }, [chartIncomeData]);

  const { data: transactionsData, isLoading: isTransactionsLoading } = useSummaryTransactionsCategory(props.report.year, props.report.month, selectedCategory.name, props.accountId);
  const [transactions, setTransactions] = useState([]);
  useEffect(() => { 
    if (transactionsData !== undefined) {
        setTransactions(transactionsData.transactionSummaries); 
        setTransactionTotalDeposit(transactionsData.depositTotal);
        setTransactionTotalWithdrawal(transactionsData.withdrawalTotal);
        setTransactionTotal(transactionsData.total);
    }
  }, [transactionsData, selectedCategory, selectedCategoryType]);

  const handleCategoryClick = (e) => {
    setSelectedCategory(e);
    setShowTransactionDialog(true);
  }

  const formatTrProps = (state = {}) => {
    //return { onClick: () => editOnClick(state.original) };
  };
  const columns = useMemo(
    () => [
      {
        Header: "Date",
        accessor: "date",
        Cell: ({ row }) => (
          date(row.original.date)
        )
      },
      {
        Header: "Payee",
        accessor: "payee",
      },
      {
        Header: "Category",
        accessor: "category",
      },
      {
        Header: "Account",
        accessor: "accountName",
      },
      {
        Header: "Bank",
        accessor: "bankName",
      },
      {
        Header: "Withdrawal",
        accessor: "withdrawal",
        Cell: ({ row }) => (
          <div className='text-right'>{row.original.withdrawal !== 0 ? currency(-1 * row.original.withdrawal) : ""}</div>
        )
      },
      {
        Header: "Deposit",
        accessor: "deposit",
        Cell: ({ row }) => (
          <div className='text-right'>{row.original.deposit !== 0 ? currency(row.original.deposit) : ""}</div>
        )
      },
    ],
    []
  );
  
  const COLORS = ['#223e7d', '#b91946'];
  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div>
      {chartIncome && 
          <div className='charts'>
            <div className='chart-header'><h2>Income</h2></div>
            <PieChart width={320} height={400}>
              <Pie
                data={chartIncome}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={100}
                dataKey="value"
              >
                {chartIncome.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colorsIncome[chartIncomeData.findIndex((item) => item.name === entry.name) % colorsIncome.length]}/>
                ))}
              </Pie>
            </PieChart>
            <div className='chart-footer'>
                <div className='chart-footer-content chart-footer-links'>
                  {chartIncome.map((e, i) => (
                    <p key={'legend-' + i} onClick={()=> handleCategoryClick(e)}><span className='chart-income' style={{backgroundColor: colorsIncome[chartIncomeData.findIndex((item) => item.name === e.name) % colorsIncome.length]}}></span><span className='chart-subtitle'>{e.name}: <span>{currency(e.value)}</span></span></p>
                  ))}            
                </div> 
            </div>
            {isChartIncomeLoading && <div className='text-center'><div className="spinner-border spinner-border-lg"></div></div> }
            <hr></hr>
          </div>
      }
      {chartExpenses && 
          <div className='charts'>
            <div className='chart-header'><h2>Expenses</h2></div>
            <PieChart width={320} height={400}>
              <Pie
                data={chartExpenses}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={100}
                dataKey="value"
              >
                {chartExpenses.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colorsExpenses[chartExpensesData.findIndex((item) => item.name === entry.name) % colorsExpenses.length]}/>
                ))}
              </Pie>
            </PieChart>
            <div className='chart-footer'>
                <div className='chart-footer-content chart-footer-links'>
                  {chartExpenses.map((e, i) => (
                    <p key={'legend-' + i} onClick={()=> handleCategoryClick(e)}><span className='chart-income' style={{backgroundColor: colorsExpenses[chartExpensesData.findIndex((item) => item.name === e.name) % colorsExpenses.length]}}></span><span className='chart-subtitle'>{e.name}: <span>{currency(e.value)}</span></span></p>
                  ))}            
                </div> 
            </div>
            {isChartIncomeLoading && <div className='text-center'><div className="spinner-border spinner-border-lg"></div></div> }
            <hr></hr>
          </div>
      }
      <div className='charts'>
        <div className='chart-header'>
          <h2>Summary</h2>
        </div>
        <PieChart width={320} height={400}>
          <Pie
            data={props.report.chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={100}
            dataKey="value"
          >
            {props.report.chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
        </PieChart>
        <div className='chart-footer'>
          <div className='chart-footer-content'>
            <p><span className='chart-income'></span><span className='chart-subtitle'>Income: <span>{currency(props.report.income)}</span></span></p>
            <p><span className='chart-expenses'></span><span className='chart-subtitle'>Expenses: <span>{currency(props.report.expenses)}</span></span></p>
            <p><span className='chart-gross'></span><span className='chart-subtitle'>Gross: <span className={props.report.gross < 0 ? 'chart-gross-loss' : 'chart-gross-gain'}>{currency(props.report.gross)}</span></span></p>
          </div>
        </div>
        <hr></hr>
      </div>

      <Modal
        size="xl"
        show={showTransactionDialog}
        onHide={() => setShowTransactionDialog(false)}
        aria-labelledby="modal-transaction-report"
      >
        <Modal.Header closeButton closeVariant='white'>
          <Modal.Title id="modal-transaction-report">
            {selectedCategory.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p><span className='chart-income'></span><span className='chart-subtitle'>Income: <span>{currency(transactionTotalDeposit)}</span></span></p>
          <p><span className='chart-expenses'></span><span className='chart-subtitle'>Expenses: <span>{currency(transactionTotalWithdrawal)}</span></span></p>
          <p><span className='chart-gross'></span><span className='chart-subtitle'>Gross: <span className={transactionTotal < 0 ? 'chart-gross-loss' : 'chart-gross-gain'}>{currency(transactionTotal)}</span></span></p>
          <hr/>
          
          {transactions && <TableBasicClick responsive columns={columns} data={transactions} formatRowProps={(state) => formatTrProps(state)} />}
          {isTransactionsLoading && <div className='text-center'><div className="spinner-border spinner-border-lg"></div></div> }
            
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={handleTransactionClose}>Close</Button>
          </Modal.Footer>
      </Modal>
    </div>
  );
}